import { enableDemoMode, disableDemoMode } from '@/lib/demo-mode'
import { communityDemoScript } from './communityDemoScript'

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function waitForSelector(selector: string, timeout = 8000) {
  const start = Date.now()

  while (Date.now() - start < timeout) {
    const el = document.querySelector(selector) as HTMLElement | null
    if (el) return el
    await wait(120)
  }

  return null
}

async function showHighlight(selector: string, label?: string, afterMs = 500) {
  window.dispatchEvent(
    new CustomEvent('community:demo-highlight', {
      detail: { selector, label },
    })
  )
  await wait(afterMs)
  return true
}

async function clearHighlight(afterMs = 200) {
  window.dispatchEvent(new CustomEvent('community:demo-highlight-clear'))
  await wait(afterMs)
  return true
}

async function clickEl(selector: string, timeout = 8000) {
  const el = await waitForSelector(selector, timeout)
  if (!el) {
    console.warn('[communityDemo] click target not found:', selector)
    return false
  }

  el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }))
  return true
}

async function inputValue(selector: string, value: string, timeout = 8000) {
  const el = (await waitForSelector(selector, timeout)) as
    | HTMLInputElement
    | HTMLTextAreaElement
    | null

  if (!el) {
    console.warn('[communityDemo] input target not found:', selector)
    return false
  }

  const setter = Object.getOwnPropertyDescriptor(
    Object.getPrototypeOf(el),
    'value'
  )?.set

  setter?.call(el, value)
  el.dispatchEvent(new Event('input', { bubbles: true }))
  el.dispatchEvent(new Event('change', { bubbles: true }))
  return true
}

async function emitEvent(name: string, detail?: any, afterMs = 500) {
  window.dispatchEvent(
    new CustomEvent(name, {
      detail: detail || {},
    })
  )
  await wait(afterMs)
  return true
}

async function playSceneAudio(audioUrl?: string | null) {
  if (!audioUrl) return

  await new Promise<void>((resolve) => {
    const audio = new Audio(audioUrl)

    const cleanup = () => {
      audio.onended = null
      audio.onerror = null
    }

    audio.onended = () => {
      cleanup()
      resolve()
    }

    audio.onerror = () => {
      cleanup()
      resolve()
    }

    audio.play().catch(() => resolve())
  })
}

async function runAction(action: any) {
  switch (action.type) {
    case 'wait':
      await wait(Number(action.ms || 500))
      return true

    case 'click': {
      const ok = await clickEl(action.selector, Number(action.timeout || 8000))
      await wait(Number(action.afterMs || 500))
      return ok
    }

    case 'input': {
      const ok = await inputValue(
        action.selector,
        String(action.value || ''),
        Number(action.timeout || 8000)
      )
      await wait(Number(action.afterMs || 300))
      return ok
    }

    case 'select-first-real-channel': {
      const realChannel = (await waitForSelector(
        '[data-channel-id]:not([data-channel-id="general"])',
        Number(action.timeout || 8000)
      )) as HTMLElement | null

      if (!realChannel) {
        console.warn('[communityDemo] no real channel found')
        return false
      }

      realChannel.click()
      await wait(Number(action.afterMs || 700))
      return true
    }

    case 'emit':
      return emitEvent(action.name, action.detail, Number(action.afterMs || 500))

    case 'highlight':
      return showHighlight(
        String(action.selector || ''),
        action.label ? String(action.label) : undefined,
        Number(action.afterMs || 500)
      )

    case 'clear-highlight':
      return clearHighlight(Number(action.afterMs || 200))

    default:
      console.warn('[communityDemo] unknown action type:', action.type)
      return false
  }
}

async function resetDemoUi() {
  window.dispatchEvent(new CustomEvent('community:demo-highlight-clear'))
  window.dispatchEvent(new CustomEvent('community:demo-reset-ui'))
  await wait(500)
}

export async function runCommunityDemo() {
  enableDemoMode()

  try {
    for (const scene of communityDemoScript) {
      window.dispatchEvent(
        new CustomEvent('community-demo:scene-start', {
          detail: { sceneId: scene.id },
        })
      )

      await resetDemoUi()

      let sceneOk = true

      if (Array.isArray(scene.actions)) {
        for (const action of scene.actions) {
          const ok = await runAction(action)
          if (!ok && action.required !== false) {
            sceneOk = false
            console.warn('[communityDemo] stopping scene because required action failed', {
              scene: scene.id,
              action,
            })
            break
          }
        }
      }

      await playSceneAudio(scene.audioUrl)

      window.dispatchEvent(
        new CustomEvent('community-demo:scene-end', {
          detail: { sceneId: scene.id, ok: sceneOk },
        })
      )
    }

    window.dispatchEvent(
      new CustomEvent('community-demo:finished', {
        detail: { script: 'owner-onboarding' },
      })
    )
  } finally {
    disableDemoMode()
  }
}